// ─────────────────────────────────────────────────────────────────────────────
// useDreamstate — GET /api/rexy/dreamstate-index. Shared core (2026-07-31).
//
// HER NIGHTLY TIER-1 SYNTHESIS (Step 3 Piece 3, Part 1 read side). Overnight she
// reasons her day into Tier-1 understanding (SYNTHESIS entries in
// memory/homepage.json, writer REXY_DREAMSTATE). A surface MIRRORS Tier 1 — it
// never synthesizes: nights newest-first, each run's entries VERBATIM; a night
// that ran empty is a fact, not a failure; unattributed Tier-1 entries (no run
// claims them) surface in their own group, never hidden.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react';
import { authHeaders, POLL_MS } from './authHeaders';
import { makeTimeFormatter } from './timeFormat';
import { DREAMSTATE } from './endpoints';

export type DreamEntry = {
  id: string;
  created_at: string | null;
  content: string;                  // her Tier-1 understanding, VERBATIM
};

export type DreamRun = {
  run_id: string;
  window_start: string | null;
  window_end: string | null;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
};

export type DreamNight = {
  night: string;                    // "YYYY-MM-DD" — the covered day, user-local
  // 'ran' entries landed; 'ran_empty' honestly quiet; 'failed' named failure;
  // 'pending' yesterday has no covering run yet.
  status: 'ran' | 'ran_empty' | 'failed' | 'pending';
  run: DreamRun | null;
  entries: DreamEntry[];
};

export type DreamstateGate = {
  tier1_total: number;
  lora_threshold: number;
  lora_progress: number;
  batches: unknown[];               // lora batches land with Part 2's composer
  store_status: string;
  ledger_status: string;
  tier1_status?: string;
  last_run_completed_at: string | null;
  next_night_pending: boolean;
};

export type DreamstateView = {
  timezone: string;
  nights: DreamNight[];             // newest-first
  unattributed: DreamEntry[];       // Tier-1 entries no run claims — never hidden
  gate: DreamstateGate;
};
export type DreamstateResponse = { success: boolean; dreamstate: DreamstateView; error?: string };

export type DreamstateFeed = {
  view: DreamstateView | null;
  err: string | null;
  updatedAt: string | null;
  timeLabel: (iso: string) => string;
  stampLabel: (iso: string) => string;
};

export function useDreamstate(): DreamstateFeed {
  const [view, setView] = useState<DreamstateView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const timeLabel = useMemo(() => makeTimeFormatter(view?.timezone), [view?.timezone]);
  const stampLabel = useMemo(() => makeTimeFormatter(view?.timezone, true), [view?.timezone]);

  useEffect(() => {
    const fetchDreamstate = () => {
      fetch(DREAMSTATE, { headers: authHeaders() })
        .then((r) => r.json())
        .then((j) => {
          if (j && j.success && j.dreamstate) {
            setView((j as DreamstateResponse).dreamstate);
            setErr(null);
            setUpdatedAt(new Date().toISOString());
          } else setErr((j && j.error) || 'unavailable');
        })
        .catch((e) => setErr(String(e)));
    };
    fetchDreamstate();
    const poll = setInterval(fetchDreamstate, POLL_MS);
    return () => clearInterval(poll);
  }, []);

  return { view, err, updatedAt, timeLabel, stampLabel };
}
